<template>
    <div
        class="custom-tooltip tooltip-workschedule"
        v-if="showTooltip"
        :style="tooltipCSS"
        ref="root"
        @mouseleave="onMouseLeave"
    >
        <div class="position" v-if="workSchedulePosition">{{ workSchedulePosition.name }}</div>
        <div class="group" v-if="workScheduleGroupLabel">{{ workScheduleGroupLabel.name }}</div>
        <div class="time">
            <template v-if="workScheduleAssignment">
                {{ formatTime(workScheduleAssignment.beginDateTime) }} - {{ formatTime(workScheduleAssignment.endDateTime) }}
            </template>
            <template v-else-if="workSchedulePosition">
                {{ formatTime(workSchedulePosition.beginDateTime) }} - {{ formatTime(workSchedulePosition.endDateTime) }}
            </template>
        </div>
        <div v-if="workScheduleAssignment">
            <CqButton
                class="remove"
                dangerous
                title="Verwijder toewijzing"
                label="Verwijder toewijzing"
                @click="$emit('remove:workScheduleAssignment', workScheduleAssignment)"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import {CSSProperties, nextTick} from 'vue';
    import {PlanbordWorkScheduleGroupLabel, PlanbordWorkSchedulePosition} from '@/types/api-object-extensions';
    import {WorkScheduleAssignment} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {CqButton} from '@cquential/ui';
    import {Component, Vue, Prop, Ref} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
        },
        emits: [
            'remove:workScheduleAssignment',
        ],
    })
    export default class TooltipWorkSchedule extends Vue {
        @Prop({required: true}) public workScheduleGroupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;

        @Ref() public readonly root!: HTMLDivElement;

        private currentElement: HTMLElement | null = null;
        private workScheduleAssignment: WorkScheduleAssignment | null = null;
        private workSchedulePosition: PlanbordWorkSchedulePosition | null = null;
        private tooltipCSS: CSSProperties = {};

        private static instance: TooltipWorkSchedule | null;

        public get showTooltip(): boolean {
            return !!this.currentElement
                && !!this.workSchedulePosition;
        }

        public get workScheduleGroupLabel(): PlanbordWorkScheduleGroupLabel | null {
            if (!this.workSchedulePosition?.workScheduleGroupLabelId) {
                return null;
            }

            return this.workScheduleGroupLabels[this.workSchedulePosition.workScheduleGroupLabelId];
        }

        public mounted(): void {
            TooltipWorkSchedule.instance = this;
        }

        public beforeUnmount(): void {
            TooltipWorkSchedule.instance = null;
        }

        public onMouseLeave(event: MouseEvent): void {
            if (this.currentElement === null) {
                return;
            }

            const target: HTMLElement = event.relatedTarget as HTMLElement;
            if (target !== this.currentElement && !this.currentElement!.contains(target)) {
                this.closeTooltip(undefined, true);
            }
        }

        public closeTooltip(relatedTarget?: HTMLElement, force: boolean = false): void {
            if (!force && relatedTarget === this.root) {
                return;
            }
            this.currentElement = null;
            this.workSchedulePosition = null;
            this.workScheduleAssignment = null;
        }

        public openTooltip(
            selectedElement: HTMLElement,
            shownWorkSchedulePosition: PlanbordWorkSchedulePosition,
            shownWorkScheduleAssignment: WorkScheduleAssignment | null,
        ): void {
            this.currentElement = selectedElement;
            this.workSchedulePosition = shownWorkSchedulePosition;
            this.workScheduleAssignment = shownWorkScheduleAssignment;
            nextTick((): void => {
                if (!this.currentElement) {
                    return;
                }

                const currentElementBoundingBox: DOMRect = this.currentElement.getBoundingClientRect();
                const bodyHeight: number = document.body.clientHeight;
                this.tooltipCSS = {};

                if (currentElementBoundingBox.bottom + this.root!.clientHeight > bodyHeight) {
                    this.tooltipCSS.bottom = (bodyHeight - currentElementBoundingBox.top) + 'px';
                } else {
                    this.tooltipCSS.top = currentElementBoundingBox.bottom + 'px';
                }

                if (currentElementBoundingBox.left + this.root!.clientWidth > document.body.clientWidth) {
                    this.tooltipCSS.right = '1rem';
                } else {
                    this.tooltipCSS.left = currentElementBoundingBox.left + 'px';
                }
            });
        }

        public formatTime(date: Date): string {
            return Dates.Formatter.formatTime(date);
        }

        public static openTooltip(targetElement: HTMLElement, shownWorkSchedulePosition: PlanbordWorkSchedulePosition, shownWorkScheduleAssignment: WorkScheduleAssignment | null = null): void {
            this.instance!.openTooltip(targetElement, shownWorkSchedulePosition, shownWorkScheduleAssignment);
        }

        public static closeTooltip(relatedTarget?: HTMLElement): void {
            this.instance!.closeTooltip(relatedTarget);
        }
    }
</script>

<style lang="scss" scoped>
    @use '@/styles/tooltip';

    .tooltip-workschedule {
        pointer-events: all;
        padding: .5rem;
        background: var(--modal-background);
        z-index: 2100;
        box-shadow: var(--work-schedule-tooltip-shadow);

        .group,
        .position,
        .remove {
            margin-bottom: .25rem;
        }

        .position {
            font-weight: bold;
        }

        .remove {
            display: inline-block;
        }
    }
</style>
